import React, { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { Printer, BookOpen, Download } from "lucide-react";
import { MANUAL_MD } from "./manualContent";

export default function ManualPage() {
  const contentRef = useRef(null);

  const handlePrint = () => {
    const printContent = contentRef.current?.innerHTML;
    if (!printContent) return;
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html lang="it">
        <head>
          <meta charset="utf-8" />
          <title>Manuale Tool Badge</title>
          <style>
            @page { size: A4; margin: 18mm 16mm; }
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0A0A0A; line-height: 1.55; max-width: 760px; margin: 0 auto; padding: 24px; }
            h1 { font-size: 28px; color: #0A0A0A; border-bottom: 3px solid #FF7A29; padding-bottom: 8px; margin-top: 0; }
            h2 { font-size: 20px; color: #0A0A0A; margin-top: 28px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; }
            h3 { font-size: 16px; color: #FF7A29; margin-top: 18px; }
            p, li { font-size: 11.5px; }
            code { background: #f4f4f4; padding: 1px 4px; border-radius: 3px; font-size: 10.5px; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
            th, td { border: 1px solid #d4d4d4; padding: 6px 8px; text-align: left; }
            th { background: #FFF5EE; color: #0A0A0A; }
            blockquote { border-left: 3px solid #FF7A29; background: #FFF5EE; padding: 8px 12px; margin: 12px 0; font-style: italic; }
            hr { border: none; border-top: 1px dashed #d4d4d4; margin: 20px 0; }
            ul, ol { padding-left: 22px; }
            a { color: #FF7A29; text-decoration: none; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 350);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="manual-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF7A29] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight" data-testid="manual-title">Manuale d'uso</h1>
              <p className="text-xs text-neutral-500">Guida completa alla piattaforma Tool Badge</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="gap-2 border-neutral-300"
              data-testid="manual-print-pdf-btn"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Stampa / Salva PDF</span>
              <span className="sm:hidden">PDF</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <article
          ref={contentRef}
          data-testid="manual-content"
          className="prose prose-neutral max-w-none
            prose-headings:font-bold prose-headings:tracking-tight
            prose-h1:text-3xl prose-h1:border-b-4 prose-h1:border-[#FF7A29] prose-h1:pb-3 prose-h1:mb-6
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:text-neutral-900
            prose-h3:text-lg prose-h3:text-[#FF7A29] prose-h3:mt-6
            prose-p:text-neutral-700 prose-p:leading-relaxed
            prose-strong:text-neutral-900
            prose-a:text-[#FF7A29] prose-a:no-underline hover:prose-a:underline
            prose-code:bg-neutral-100 prose-code:text-neutral-900 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-[''] prose-code:after:content-['']
            prose-table:text-sm prose-table:border prose-table:border-neutral-300
            prose-th:bg-[#FFF5EE] prose-th:text-neutral-900 prose-th:font-semibold prose-th:p-2 prose-th:border prose-th:border-neutral-300
            prose-td:p-2 prose-td:border prose-td:border-neutral-200
            prose-blockquote:border-l-4 prose-blockquote:border-[#FF7A29] prose-blockquote:bg-[#FFF5EE] prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:text-neutral-700
            prose-hr:border-dashed prose-hr:border-neutral-300
            prose-ul:my-3 prose-li:my-1"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{MANUAL_MD}</ReactMarkdown>
        </article>

        {/* Footer CTA */}
        <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-[#0A0A0A] to-neutral-800 text-white print:hidden" data-testid="manual-footer-cta">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-bold mb-1">Hai bisogno di aiuto?</h3>
              <p className="text-sm text-white/70">Vuoi una versione offline da consultare ovunque? Salva il manuale in PDF.</p>
            </div>
            <Button
              onClick={handlePrint}
              className="bg-[#FF7A29] hover:bg-[#FF6B14] text-white gap-2"
              data-testid="manual-download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              Scarica PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
